import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { makeStyles, CircularProgress } from '@material-ui/core'
import StepRow from './StepRow'

// Phase-agnostic filler so every phrase is plausible at any point in the run.
const INVESTIGATING_PHRASES = [
  'Thinking…',
  'Crunching numbers…',
  'Working through the data…',
  'Sanity-checking the math…',
  'Considering alternatives…',
  'Cross-checking…',
  'Examining details…',
  'Looking for patterns…',
  'Comparing values…',
  'Reviewing fetched data…',
  'Sampling the audit trail…',
  'Following the data…',
  'Verifying calculations…',
  'Tracing values…',
  'Inspecting holdings…',
  'Stepping through the math…',
  'Checking assumptions…',
  'Sifting through results…',
  'Looking for what changed…',
  'Working through edge cases…'
]
const PHRASE_ROTATE_MS = 4500

const useStyles = makeStyles(() => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8
  },
  empty: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '14px 12px',
    border: '1px dashed var(--color-neutral-300, #d1d5db)',
    borderRadius: 4,
    color: 'var(--color-neutral-500, #6b7280)',
    fontSize: 13
  },
  busyHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '0 4px',
    fontSize: 12,
    color: 'var(--color-neutral-500, #6b7280)'
  },
  busyPhrase: {
    transition: 'opacity 240ms ease'
  }
}))

export default function InvestigationTimeline ({ steps, busyLabel, cycle }) {
  const classes = useStyles()
  const hasSteps = Array.isArray(steps) && steps.length > 0
  const showWaitingForInvestigator = cycle && !hasSteps
  const rotatingPhrase = useRotatingPhrase(
    INVESTIGATING_PHRASES,
    PHRASE_ROTATE_MS,
    cycle && hasSteps
  )
  const headerLabel = cycle ? rotatingPhrase : busyLabel
  const showBusyHeader = !!headerLabel && !showWaitingForInvestigator

  return (
    <div className={classes.root}>
      {showBusyHeader && (
        <div className={classes.busyHeader}>
          <CircularProgress size={12} />
          <span key={headerLabel} className={classes.busyPhrase}>{headerLabel}</span>
        </div>
      )}
      {showWaitingForInvestigator && (
        <div className={classes.empty}>
          <CircularProgress size={14} />
          <span>Waiting for a Denali AI investigator to become available&hellip;</span>
        </div>
      )}
      {hasSteps && steps.map(step => (
        <StepRow key={step.id || step.title} step={step} />
      ))}
    </div>
  )
}

function useRotatingPhrase (phrases, intervalMs, enabled) {
  const [index, setIndex] = useState(() => Math.floor(Math.random() * phrases.length))

  useEffect(() => {
    if (!enabled || phrases.length <= 1) return undefined
    const handle = setInterval(() => {
      setIndex(prev => {
        let next = Math.floor(Math.random() * (phrases.length - 1))
        if (next >= prev) next += 1
        return next
      })
    }, intervalMs)
    return () => clearInterval(handle)
  }, [enabled, phrases, intervalMs])

  return phrases[index]
}

InvestigationTimeline.propTypes = {
  steps: PropTypes.arrayOf(PropTypes.object),
  busyLabel: PropTypes.string,
  cycle: PropTypes.bool
}
